import { useEffect, useState } from 'react';
import { Modal, DatePicker, Button, Form } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { AddIcon } from '../../../../icons';
import dayjs from 'dayjs';
import {
  encounterDetailsSlice,
  newEncounterfromMicSlice,
} from '../../store/slices/encounter';
import { createEncounter } from '../../store/actions/encounter';

const CreateEncounterModalFlexible = ({
  isOpen,
  setIsOpen,
  callback,
  dateInfo,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);

  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );

  const { isEncounterCreateLoading } = useSelector(
    (state) => state?.encounterState
  );

  // Initialize form with date info when modal opens
  useEffect(() => {
    if (isOpen && dateInfo?.dateStr) {
      form.setFieldsValue({
        start_date: dayjs(dateInfo.dateStr),
        description: `Encounter - ${dayjs(dateInfo.dateStr).format('M/D h:mm A')}`,
      });
    }
  }, [isOpen, dateInfo, form]);

  // Handle encounter creation loading state
  useEffect(() => {
    if (isEncounterCreateLoading) {
      setSubmitLoading(false);
      setIsOpen(false);
    }
  }, [isEncounterCreateLoading, setIsOpen]);

  const handleEncounterPayload = (dateValue) => {
    const parsedDate = dayjs(dateValue);
    const formattedDate = parsedDate.toISOString();
    const month = parsedDate.month() + 1;
    const day = parsedDate.date();
    const hour = parsedDate.hour();
    const minute = parsedDate.minute();
    const ampm = parsedDate.format('A');
    const description = `Encounter - ${month}/${day} ${hour}:${minute.toString().padStart(2, '0')} ${ampm}`;

    return { formattedDate, description };
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitLoading(true);
      dispatch(newEncounterfromMicSlice(true));
      dispatch(encounterDetailsSlice(null));

      const { formattedDate, description } = handleEncounterPayload(
        values.start_date
      );

      const data = {
        description: description,
        practitioner_id: currentPractitioner?.practitioner_id,
        start_time: formattedDate,
      };

      await dispatch(createEncounter(data, callback));
    } catch (error) {
      console.error('Error creating encounter:', error);
    } finally {
      setSubmitLoading(false);
      setIsOpen(false);
    }
  };

  const handleCancel = () => {
    setSubmitLoading(false);
    setIsOpen(false);
    form.resetFields();
  };

  return (
    <Modal
      open={isOpen}
      title={
        <div className="flex items-center text-[20px]">
          <AddIcon className="mr-2" /> Create Encounter?
        </div>
      }
      onCancel={handleCancel}
      footer={null}
      maskClosable={true}
      className="create-encounter-modal"
      style={{ top: '30%' }}
    >
      <div className="py-4">
        <p className="modal__form-el-label mb-4">
          Select date and time to schedule the encounter.
        </p>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            start_date: dateInfo?.dateStr ? dayjs(dateInfo.dateStr) : null,
          }}
        >
          <Form.Item
            name="start_date"
            rules={[{ required: true, message: 'Please select date and time' }]}
          >
            <DatePicker
              showTime={{
                format: 'hh:mm a',
                use12Hours: true,
                minuteStep: 5,
              }}
              format="MM-DD-YYYY hh:mm a"
              placeholder="MM/DD/YYYY hh:mm AM/PM"
              placement="bottomRight"
              className="custom-date-picker w-full h-[48px] rounded-[5px] text-[#92928F] flex items-center justify-between px-[16px]"
              disabledDate={(current) => {
                // Disable past dates
                return current && current < dayjs().startOf('day');
              }}
              disabledTime={(date) => {
                if (date && date.isSame(dayjs(), 'day')) {
                  // For today, disable past hours and minutes
                  const now = dayjs();
                  return {
                    disabledHours: () =>
                      Array.from({ length: now.hour() }, (_, i) => i),
                    disabledMinutes: (selectedHour) => {
                      if (selectedHour === now.hour()) {
                        return Array.from(
                          { length: now.minute() },
                          (_, i) => i
                        );
                      }
                      return [];
                    },
                  };
                }
                return {};
              }}
            />
          </Form.Item>

          <div className="flex items-center justify-end gap-3 mt-6">
            <Button
              onClick={handleCancel}
              className="cancel-button"
              disabled={submitLoading}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitLoading}
              className="ih-button-primary"
            >
              Create
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default CreateEncounterModalFlexible;
